import React, { useEffect, useState } from 'react';
import { useMutation, type DocumentNode } from '@apollo/client';
import { takenLine, takenNote } from './homeGap';

/**
 * "Recently deleted" (pages, articles and forms): what was deleted in the last 30
 * days, each with a Restore button. A restored item comes back as a draft at
 * its old address. A page whose address a new page now has (`takenBy`) says
 * so instead, and stays in history: the new page keeps the address
 * ("tinycms UX optimization review v12", Prototype v7).
 * Nothing renders when the list is empty.
 *
 * `collapsible` (Website › Pages rail, 250b5bcc): starts closed as one row,
 * "RECENTLY DELETED", a count and a chevron; the row opens the list. Open or
 * closed is kept for the session (sessionStorage, per `collapsible` key).
 */

export interface DeletedItem {
  id: string;
  title: string;
  path: string | null;
  /** A line in place of the path ("3 responses kept"). */
  detail?: string | null;
  deletedAt: string;
  daysLeft: number;
  /** Pages: the page now at its address, when one is. */
  takenBy?: { id: string; title: string | null; live: boolean } | null;
  /**
   * Pages: the free address it comes back at, when the home page (shown at /)
   * now has its own one (QA round 30, da7bcc29).
   */
  restoreAs?: string | null;
}

/** "Comes back at /home-2: /home now opens your home page." */
export const restoreAsLine = (d: DeletedItem) =>
  d.restoreAs ? `Comes back at ${d.restoreAs}: ${d.path} now opens your home page.` : null;

const readOpen = (key: string) => {
  try {
    return sessionStorage.getItem(key) === 'open';
  } catch {
    return false;
  }
};

const daysLeftText = (d: DeletedItem) =>
  `${d.daysLeft} day${d.daysLeft === 1 ? '' : 's'} left to restore`;

export function RecentlyDeleted({
  accountId,
  items,
  restoreMutation,
  onRestored,
  note,
  variant = 'rail',
  collapsible,
  openWhen = 0,
}: {
  accountId: string;
  items: DeletedItem[];
  restoreMutation: DocumentNode;
  onRestored: (id: string) => void;
  note: string;
  /** rail: the Pages side list; list: under the article list. */
  variant?: 'rail' | 'list';
  /** Start collapsed; the session key that remembers open or closed. */
  collapsible?: string;
  /** Changed by the parent to open it (Forms after a delete: Round 36). */
  openWhen?: number;
}) {
  const [restore] = useMutation(restoreMutation);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(() => !collapsible || readOpen(collapsible));
  useEffect(() => {
    if (!openWhen) return;
    setOpen(true);
    try {
      if (collapsible) sessionStorage.setItem(collapsible, 'open');
    } catch {
      // Storage blocked: it opens, just isn't remembered.
    }
  }, [openWhen, collapsible]);
  if (!items.length) return null;
  const rail = variant === 'rail';
  const taken = takenNote(items);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      if (collapsible) sessionStorage.setItem(collapsible, next ? 'open' : 'closed');
    } catch {
      // Storage blocked: it still opens, just isn't remembered.
    }
  };
  return (
    <div style={rail ? undefined : { margin: '8px 0 32px', padding: '0 12px' }}>
      {collapsible ? (
        <div
          className="pg-rail-head pg-deleted-toggle"
          role="button"
          tabIndex={0}
          aria-expanded={open}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle();
            }
          }}
        >
          <span>RECENTLY DELETED</span>
          <span className="pg-deleted-count">{items.length}</span>
          <span className="pg-deleted-chevron" aria-hidden="true">
            ›
          </span>
        </div>
      ) : null}
      {open ? (
        <RecentlyDeletedList
          {...{ items, rail, taken, note, busy, error, collapsible }}
          onRestore={async (d) => {
            setBusy(d.id);
            setError(null);
            try {
              await restore({ variables: { accountId, id: d.id } });
              onRestored(d.id);
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Couldn’t restore it');
            } finally {
              setBusy(null);
            }
          }}
        />
      ) : null}
    </div>
  );
}

function RecentlyDeletedList({
  items,
  rail,
  taken,
  note,
  busy,
  error,
  collapsible,
  onRestore,
}: {
  items: DeletedItem[];
  rail: boolean;
  taken: string | null;
  note: string;
  busy: string | null;
  error: string | null;
  collapsible?: string;
  onRestore: (d: DeletedItem) => void;
}) {
  return (
    <>
      {collapsible ? null : (
        <div
          className={rail ? 'pg-rail-head' : undefined}
          style={
            rail
              ? undefined
              : {
                  font: '600 11px var(--ink-font)',
                  letterSpacing: '0.08em',
                  color: 'var(--ink-ink3)',
                  margin: '0 0 6px',
                }
          }
        >
          RECENTLY DELETED
        </div>
      )}
      {items.map((d) => (
        <div
          key={d.id}
          className={rail ? 'pg-rail-item' : undefined}
          style={
            rail
              ? { cursor: 'default' }
              : {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 0',
                  borderTop: '1px solid var(--ink-line)',
                }
          }
        >
          <span
            className={rail ? 'pg-rail-text' : undefined}
            style={rail ? undefined : { flex: 1, minWidth: 0 }}
          >
            <span
              className={rail ? 'pg-rail-title' : undefined}
              style={{ color: 'var(--ink-ink3)', display: rail ? undefined : 'block' }}
            >
              {d.title || 'Untitled'}
            </span>
            {rail ? (
              // The rail is narrow: the deadline gets its own line so Restore
              // can't squeeze it into "26 days l…" (QA 2026-10-21).
              <>
                {d.detail || d.path ? (
                  <span className="pg-rail-path">{d.detail || d.path}</span>
                ) : null}
                {d.takenBy ? null : <span className="pg-rail-sub">{daysLeftText(d)}</span>}
                {d.restoreAs ? <span className="pg-rail-sub">{restoreAsLine(d)}</span> : null}
              </>
            ) : (
              <span style={{ fontSize: 12, color: 'var(--ink-ink3)' }}>
                {d.takenBy
                  ? d.detail || d.path
                  : `${d.detail || d.path ? `${d.detail || d.path} · ` : ''}${daysLeftText(d)}`}
                {d.restoreAs ? <span style={{ display: 'block' }}>{restoreAsLine(d)}</span> : null}
              </span>
            )}
            {d.takenBy ? (
              <span
                className={`pg-deleted-taken${rail ? ' pg-rail-sub' : ''}`}
                style={rail ? undefined : { display: 'block', fontSize: 12 }}
              >
                {takenLine(d)}
              </span>
            ) : null}
          </span>
          {d.takenBy ? null : (
            <button
              type="button"
              className="ink-btn"
              style={{ padding: '2px 10px', fontSize: 12 }}
              disabled={busy === d.id}
              aria-label={`Restore ${d.title}`}
              onClick={() => onRestore(d)}
            >
              {busy === d.id ? 'Restoring…' : 'Restore'}
            </button>
          )}
        </div>
      ))}
      {taken ? (
        <p
          className="pg-note"
          style={{ margin: rail ? '6px 12px' : '6px 0', fontSize: 12, textAlign: 'left' }}
        >
          {taken}
        </p>
      ) : null}
      {error ? (
        <p role="alert" style={{ color: 'var(--ink-red)', fontSize: 12, margin: '6px 12px' }}>
          {error}
        </p>
      ) : null}
      <p
        className="pg-note"
        style={{ margin: rail ? '6px 12px' : '6px 0', fontSize: 12, textAlign: 'left' }}
      >
        {note}
      </p>
    </>
  );
}
